/**
 * La base local (IndexedDB vía Dexie). **Es la fuente de la que lee toda la UI.**
 *
 * Ninguna pantalla consulta la red para pintarse. Eso es lo que hace que §4.1 sea cumplible:
 * cada serie se guarda al momento de tocarla, en local, y sobrevive al cierre forzado de la app
 * porque ya está en disco antes de que la red se entere.
 *
 * ### La cola de sync guarda CLAVES SUCIAS, no operaciones
 *
 * Cuando algo cambia se anota `{tabla, clave}`. Al sincronizar se lee el estado ACTUAL de esa
 * fila y se manda. No hay log de operaciones que reproducir.
 *
 * Es la decisión más importante de este archivo, y la razón es que hace la sincronización
 * idempotente y conmutativa por construcción: mandar dos veces el estado de una fila da el
 * mismo resultado que mandarlo una, y el orden entre filas distintas no importa. Un log de
 * operaciones ("editá el peso a 30", "reasigná el ejercicio") exige orden exacto y entrega
 * exactamente-una-vez, que es justo lo que una red de gimnasio no da.
 *
 * El costo: se pierde la intención intermedia. Si edito el peso tres veces sin conexión, se
 * sincroniza sólo el valor final. Para esta app eso es lo correcto — nadie quiere ver los tres
 * intentos.
 */

import Dexie, { type Table } from 'dexie';
import type {
  Alimento,
  DiaBloqueado,
  Ejercicio,
  Gimnasio,
  IngestaImagen,
  Maquina,
  MedidaCorporal,
  Plan,
  RegistroCardio,
  Rutina,
  RutinaDia,
  RutinaEjercicio,
  Serie,
  Sesion,
} from '@mg/core';
import type { Perfil } from '@mg/sheets';

/** Nombres de tabla que se sincronizan; coinciden con las pestañas de la planilla. */
export type TablaSync =
  | 'perfil'
  | 'ejercicios_propios'
  | 'planes'
  | 'dias_bloqueados'
  | 'rutinas'
  | 'rutina_dias'
  | 'rutina_ejercicios'
  | 'sesiones'
  | 'series'
  | 'cardio'
  | 'medidas'
  | 'comidas'
  | 'ingestas_imagen';

export interface Sucio {
  /** `${tabla}:${clave}` — así una misma fila marcada dos veces ocupa una sola entrada. */
  id: string;
  tabla: TablaSync;
  clave: string;
  marcadoEn: number;
  intentos: number;
  /** Último error, para poder mostrarle al usuario por qué algo no subió. */
  ultimoError: string | null;
}

export interface Meta {
  clave: string;
  valor: unknown;
}

export class BaseMG extends Dexie {
  perfil!: Table<Perfil, string>;
  ejercicios!: Table<Ejercicio, string>;
  gimnasios!: Table<Gimnasio, string>;
  maquinas!: Table<Maquina, string>;
  planes!: Table<Plan, string>;
  diasBloqueados!: Table<DiaBloqueado, string>;
  rutinas!: Table<Rutina, string>;
  rutinaDias!: Table<RutinaDia, string>;
  rutinaEjercicios!: Table<RutinaEjercicio, string>;
  sesiones!: Table<Sesion, string>;
  series!: Table<Serie, string>;
  cardio!: Table<RegistroCardio, string>;
  medidas!: Table<MedidaCorporal, string>;
  comidas!: Table<Alimento, string>;
  ingestas!: Table<IngestaImagen, string>;
  sucios!: Table<Sucio, string>;
  meta!: Table<Meta, string>;

  constructor(nombre = 'mg') {
    super(nombre);
    this.version(1).stores({
      perfil: 'id',
      ejercicios: 'id, musculo_primario, es_global',
      gimnasios: 'id',
      maquinas: 'id, gimnasio_id, ejercicio_id',
      planes: 'id, fecha_vencimiento',
      diasBloqueados: 'id, fecha',
      rutinas: 'id, activa',
      rutinaDias: 'id, rutina_id, orden',
      rutinaEjercicios: 'id, rutina_dia_id, orden',
      sesiones: 'id, fecha, estado, plan_id, rutina_dia_id',
      // idem_key indexada: es por donde se deduplica y por donde se hace upsert.
      series: 'id, sesion_id, ejercicio_id, idem_key',
      cardio: 'id, sesion_id, idem_key',
      medidas: 'id, fecha, idem_key',
      ingestas: 'id, tipo, creada_en',
      sucios: 'id, tabla, marcadoEn',
      meta: 'clave',
    });

    // Agregar una tabla exige una versión nueva: sin esto, IndexedDB abre con el esquema viejo y
    // `db.comidas` falla en runtime para cualquiera que ya tenga la base creada.
    // fecha indexada porque la pantalla de nutrición consulta siempre por día.
    this.version(2).stores({
      comidas: 'id, fecha, momento, idem_key',
    });
  }
}

export const db = new BaseMG();

/**
 * Acceso a la base local desde la consola y desde los scripts de verificación.
 *
 * En dev siempre; en un build sólo si se compiló con `VITE_MG_DEBUG=1`, que es lo que hace
 * `npm run verificar` para poder comprobar el offline contra el service worker real. El build que
 * se despliega no lleva esto: no hay ninguna razón para exponer los datos de salud del usuario en
 * `window`.
 */
// Acceso por punto y no por corchetes: es la única forma en que Vite reemplaza el valor al
// compilar y puede eliminar la rama entera. Con corchetes, este bloque viaja al bundle de
// producción y expone la base local de datos de salud en `window`.
if (import.meta.env.DEV || import.meta.env.VITE_MG_DEBUG === '1') {
  (globalThis as unknown as { __mg?: unknown }).__mg = {
    db,
    async contar(tabla: keyof BaseMG & string) {
      return (db[tabla] as unknown as Table<unknown, string>).count();
    },
    async todo(tabla: keyof BaseMG & string) {
      return (db[tabla] as unknown as Table<unknown, string>).toArray();
    },
  };
}

/** Mapa tabla de sync → tabla de Dexie. Los nombres difieren en dos casos por legibilidad. */
export const TABLA_DEXIE: Record<TablaSync, keyof BaseMG & string> = {
  perfil: 'perfil',
  ejercicios_propios: 'ejercicios',
  planes: 'planes',
  dias_bloqueados: 'diasBloqueados',
  rutinas: 'rutinas',
  rutina_dias: 'rutinaDias',
  rutina_ejercicios: 'rutinaEjercicios',
  sesiones: 'sesiones',
  series: 'series',
  cardio: 'cardio',
  medidas: 'medidas',
  comidas: 'comidas',
  ingestas_imagen: 'ingestas',
};

/**
 * Marca una fila como pendiente de subir. Se llama SIEMPRE junto a la escritura local, dentro
 * de la misma transacción: si se separan, un cierre de la app entre las dos deja un cambio
 * guardado que nunca se sincroniza.
 */
export async function marcarSucio(tabla: TablaSync, clave: string): Promise<void> {
  await db.sucios.put({
    id: `${tabla}:${clave}`,
    tabla,
    clave,
    marcadoEn: Date.now(),
    intentos: 0,
    ultimoError: null,
  });
}

/** Escritura local + marca, atómicas. Es el único camino para modificar datos del usuario. */
export async function guardarLocal<T extends { id: string }>(
  tabla: TablaSync,
  filas: T[],
): Promise<void> {
  if (filas.length === 0) return;
  const nombreDexie = TABLA_DEXIE[tabla];
  const tablaDexie = db[nombreDexie] as unknown as Table<T, string>;

  await db.transaction('rw', tablaDexie, db.sucios, async () => {
    await tablaDexie.bulkPut(filas);
    await db.sucios.bulkPut(
      filas.map((f) => ({
        id: `${tabla}:${f.id}`,
        tabla,
        clave: f.id,
        marcadoEn: Date.now(),
        intentos: 0,
        ultimoError: null as string | null,
      })),
    );
  });
}

export async function leerMeta<T>(clave: string, porDefecto: T): Promise<T> {
  const fila = await db.meta.get(clave);
  return fila === undefined ? porDefecto : (fila.valor as T);
}

export async function escribirMeta(clave: string, valor: unknown): Promise<void> {
  await db.meta.put({ clave, valor });
}

/** Borra todo lo local. Para "salir" y para el botón de reinicio en Ajustes. */
export async function limpiarTodo(): Promise<void> {
  await db.delete();
  await db.open();
}
